import { resolveEnabledModules, type DemoRole, type MeOutput, type ModuleKey } from '@garage/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '../../shared/api.js';
import { PLATFORM_QUERY_KEY } from '../../public/platform.js';
import { useEnterSession } from './session.js';

/**
 * Vague 7 : démonstration (générique depuis la page de connexion, ou lien brandé `/demo/lien#<jeton>`) et
 * modules activables, côté interface. Le serveur reste seul juge : session de démonstration, refus
 * (`demo_forbidden`), modules désactivés (`module_disabled`).
 */

/**
 * Démonstration générique ouverte sur ce serveur (`public.platform.demoEnabled`, `DEMO_ENABLED`). Même clé
 * de cache que le nom du produit : aucune requête de plus sur la page de connexion. `null` pendant le
 * chargement ; `false` si l'API ne l'annonce pas ou ne répond pas (le bouton n'apparaît jamais à tort).
 */
export function useDemoEnabled(): boolean | null {
  const q = useQuery({
    queryKey: PLATFORM_QUERY_KEY,
    queryFn: () => api('public.platform'),
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
  });
  if (q.isPending) return null;
  return q.data?.demoEnabled === true;
}

// ─── Jeton d'un lien de démonstration ───────────────────────────────────────

const LINK_TOKEN_KEY = 'demo-link-token';

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * Lit le jeton du lien dans le **fragment** (`#…`, jamais envoyé au serveur ni dans le Referer), puis
 * l'efface aussitôt de la barre d'adresse (`history.replaceState`) : il ne reste ni dans l'historique ni
 * dans une capture d'écran. Gardé pour l'onglet seulement (`sessionStorage`) afin qu'un rechargement de la
 * page d'arrivée fonctionne ; oublié à l'entrée dans la démonstration.
 */
export function takeDemoLinkToken(): string | null {
  if (typeof window === 'undefined') return null;
  let raw = window.location.hash.replace(/^#/, '');
  try {
    raw = decodeURIComponent(raw);
  } catch {
    // fragment mal encodé : utilisé tel quel
  }
  const fromHash = raw.trim();
  if (window.location.hash) {
    window.history.replaceState(window.history.state, '', window.location.pathname);
  }
  const store = storage();
  if (fromHash) {
    try {
      store?.setItem(LINK_TOKEN_KEY, fromHash);
    } catch {
      // stockage indisponible : le jeton reste en mémoire pour cette page
    }
    return fromHash;
  }
  try {
    return store?.getItem(LINK_TOKEN_KEY) ?? null;
  } catch {
    return null;
  }
}

export function forgetDemoLinkToken(): void {
  try {
    storage()?.removeItem(LINK_TOKEN_KEY);
  } catch {
    // rien à faire
  }
}

// ─── Entrée, changement de rôle ─────────────────────────────────────────────

/** Démonstration générique (`demo.enter`) : session sans compte, puis l'espace garage de démonstration. */
export function useEnterDemo() {
  const enter = useEnterSession();
  return useMutation({
    mutationFn: (role: DemoRole) => api('demo.enter', { body: { role } }),
    onSuccess: (me) => enter(me),
  });
}

/** Lien brandé (`demo.link.enter`) : le rôle choisi, ou celui du lien. */
export function useEnterDemoLink(token: string | null) {
  const enter = useEnterSession();
  return useMutation({
    mutationFn: (role: DemoRole | undefined) =>
      api('demo.link.enter', { body: role ? { token: token ?? '', role } : { token: token ?? '' } }),
    onSuccess: (me) => {
      forgetDemoLinkToken();
      return enter(me);
    },
  });
}

/** Changer de rôle dans la même démonstration (même garage, même échéance) ; retour à l'Accueil. */
export function useSwitchDemoRole() {
  const enter = useEnterSession();
  return useMutation({
    mutationFn: (role: DemoRole) => api('demo.switchRole', { body: { role } }),
    onSuccess: (me) => enter(me),
  });
}

// ─── Modules activables ─────────────────────────────────────────────────────

/**
 * Modules actifs du garage de la session (`auth.me`). Absents (ancienne réponse, session support) : les
 * valeurs par défaut du registre partagé.
 */
export function enabledModules(me: Pick<MeOutput, 'active'> | null | undefined): Set<ModuleKey> {
  const list = me?.active?.enabledModules;
  return list ? new Set(list) : resolveEnabledModules(null);
}

export function isModuleEnabled(me: Pick<MeOutput, 'active'> | null | undefined, key: ModuleKey): boolean {
  return enabledModules(me).has(key);
}
